import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Typography,
} from '@mui/material';
import { ReadinessStatus, ConfidenceLevel, TrustOverrideDialogProps } from '../../types/readinessConfidence.types';

export default function TrustOverrideDialog({
  open,
  onClose,
  currentReadiness,
  currentConfidence,
  onSubmitOverride,
  loading = false,
}: TrustOverrideDialogProps) {
  const [readiness, setReadiness] = useState<ReadinessStatus | ''>('');
  const [confidence, setConfidence] = useState<ConfidenceLevel | ''>('');
  const [reason, setReason] = useState('');

  const handleClose = () => {
    setReadiness('');
    setConfidence('');
    setReason('');
    onClose();
  };

  const handleSubmit = () => {
    if (!reason.trim()) {
      return;
    }
    const data: any = { reason };
    if (readiness) data.readiness = readiness;
    if (confidence) data.confidence = confidence;
    onSubmitOverride(data);
  };

  const hasChanges = (readiness && readiness !== currentReadiness) || 
    (confidence && confidence !== currentConfidence);
  const isValid = hasChanges && reason.trim().length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Override Trust Indicator</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Alert severity="warning">
            Admin override will override automatic calculations. All overrides are logged for audit purposes.
          </Alert>

          {/* Current Values */}
          <Stack>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'textSecondary', mb: 1 }}>
              Current Values
            </Typography>
            <Typography variant="body2">
              Readiness: <strong>{currentReadiness || 'Not set'}</strong> | Confidence:{' '}
              <strong>{currentConfidence || 'Not set'}</strong>
            </Typography>
          </Stack>

          {/* Readiness Override */}
          <FormControl fullWidth>
            <InputLabel>Readiness Status</InputLabel>
            <Select
              value={readiness}
              label="Readiness Status"
              onChange={(e) => setReadiness(e.target.value as ReadinessStatus)}
              disabled={loading}
            >
              <MenuItem value=""><em>No change</em></MenuItem>
              <MenuItem value="PASS">PASS - All validation checks passed</MenuItem>
              <MenuItem value="WARN">WARN - Some validation warnings</MenuItem>
              <MenuItem value="FAIL">FAIL - Critical validation failures</MenuItem>
            </Select>
          </FormControl>

          {/* Confidence Override */}
          <FormControl fullWidth>
            <InputLabel>Confidence Level</InputLabel>
            <Select
              value={confidence}
              label="Confidence Level"
              onChange={(e) => setConfidence(e.target.value as ConfidenceLevel)}
              disabled={loading}
            >
              <MenuItem value=""><em>No change</em></MenuItem>
              <MenuItem value="HIGH">HIGH - Strong statistical evidence (p&lt;0.01)</MenuItem>
              <MenuItem value="MEDIUM">MEDIUM - Moderate evidence (p&lt;0.05)</MenuItem>
              <MenuItem value="LOW">LOW - Limited evidence</MenuItem>
              <MenuItem value="INSUFFICIENT">INSUFFICIENT - Not enough data</MenuItem>
            </Select>
          </FormControl>

          {/* Override Reason */}
          <TextField
            label="Override Reason"
            multiline
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why you are overriding the automatic trust calculation..."
            disabled={loading}
            helperText="Required. This will be logged for audit purposes."
            required
          />

          {loading && (
            <Alert severity="info">Processing override...</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || loading}
        >
          Apply Override
        </Button>
      </DialogActions>
    </Dialog>
  );
}
